import { makeStyles } from "tss-react/mui";
import { alphaColor } from "@miplatform/utils";
import { Box, Collapse, alpha, useTheme } from "@mui/material";
import { CardInfo } from "../CardInfo/CardInfo";
import { MiFieldSetStacked } from "../MiFieldSet/MiFieldSetStacked";
import { MiIcon } from "../MiIcon/MiIcon";

/**
 * @typedef {Object} MiCardListProps
 * @property {Object} item
 * @property {number} [index]
 * @property {Function} [isAlert]
 * @property {Function} [alertColor]
 * @property {Function} [isLate]
 * @property {React.ReactNode} [children]
 * @property {Function} [onClick]
 * @property {Array<Object>} [sections]
 * @property {string} [title]
 * @property {string} [titleField]
 * @property {Object} [cardContainerStyles]
 * @property {Object} [containerStyles]
 * @property {Function} [renderItemActions]
 * @property {string} [testID]
 * @property {Function} [renderTitle]
 * @property {string} [cardContainerJustifyContent]
 * @property {Object} [leftCardProps]
 * @property {boolean} [showNulls]
 * @property {Function|null} [rightAction]
 * @property {boolean} [showRightAction]
 * @property {Object} [flippedProps]
 */

/**
 * @component
 * @param {MiCardListProps} props - The component props
 * @param {Object} props.item - The data item to display in the card
 * @param {number} [props.index] - The index of the item in a list (used for animations)
 * @param {Function} [props.isAlert] - Function that determines if the item should be styled as an alert. Receives the item as parameter and returns boolean
 * @param {Function} [props.alertColor] - Function that returns the alert color for the item. Receives the item as parameter and returns a color string
 * @param {Function} [props.isLate] - Function that determines if the item should be styled as late. Receives the item as parameter and returns boolean
 * @param {React.ReactNode} [props.children] - Additional content to render below the card container
 * @param {Function} [props.onClick] - Click handler function that receives the item as parameter
 * @param {Array<Object>} [props.sections] - Array of section objects defining field sets to display
 * @param {Object} [props.sections[].fieldset] - Field set configuration for a section
 * @param {string} [props.sections[].testId] - Test ID for the section
 * @param {Object} [props.sections[].fieldSetStyle] - Custom styles for the field set
 * @param {string} [props.title] - Static title text to display
 * @param {string} [props.titleField] - Field name in the item object to use as title
 * @param {Object} [props.cardContainerStyles] - Custom styles for the card container
 * @param {Object} [props.containerStyles] - Custom styles for the outer container
 * @param {Function} [props.renderItemActions] - Function to render action buttons/components, receives the item as parameter
 * @param {string} [props.testID] - Test ID for the component
 * @param {Function} [props.renderTitle] - Custom function to render the title, receives the item as parameter
 * @param {string} [props.cardContainerJustifyContent="space-between"] - CSS justify-content value for the card container
 * @param {Object} [props.leftCardProps={}] - Configuration object for the left card section
 * @param {string} [props.leftCardProps.color] - Color for the left card icon/abbreviation
 * @param {string} [props.leftCardProps.backgroundColor] - Background color for the left card icon container
 * @param {string} [props.leftCardProps.icon] - MDI icon path for the left card
 * @param {string} [props.leftCardProps.iconTestId="mi-card-left-icon"] - Test ID for the left card icon
 * @param {string} [props.leftCardProps.abbreviation] - Static abbreviation text to display
 * @param {string} [props.leftCardProps.abbreviationField] - Field name in item to use for abbreviation
 * @param {number} [props.leftCardProps.size=12] - Size of the icon
 * @param {number|string} [props.leftCardProps.leftWidth] - Width constraint for the left card section
 * @param {Array<Object>} [props.leftCardProps.fieldset] - Field set configuration for the left card
 * @param {boolean} [props.showNulls=true] - Whether to display fields with null/empty values
 * @param {Function} [props.rightAction] - Function that returns JSX for right action area, receives (item, index) as parameters
 * @param {boolean} [props.showRightAction=false] - Controls visibility of the right action collapse
 * @param {Object} [props.flippedProps={}] - Props from react-flip-toolkit for animation support
 * @returns {JSX.Element} The rendered card list component
 */
export function MiCardList({
  item,
  index,
  isAlert,
  alertColor,
  isLate,
  children,
  onClick,
  sections,
  title,
  titleField,
  cardContainerStyles,
  containerStyles,
  renderItemActions,
  testID,
  renderTitle,
  cardContainerJustifyContent = "space-between",
  leftCardProps = {},
  showNulls = true,
  rightAction = null,
  showRightAction = false,
  flippedProps = {}, // required by react-flip-toolkit
}) {
  const theme = useTheme();

  const {
    color = theme.palette.grey.main,
    backgroundColor = alpha(color, 0.15),
    icon = "",
    iconTestId = "mi-card-left-icon",
    abbreviation = "",
    abbreviationField = "",
    size = 12,
    leftWidth,
    leftMaxWidth,
  } = leftCardProps;

  const { classes } = useStyles({
    color,
    leftWidth,
    leftMaxWidth,
    ...(isAlert && { isAlert: isAlert(item) }),
    ...(alertColor && { alertColor: alertColor(item) }),
    ...(isLate && { isLate: isLate(item) }),
  });

  const handleOnClick = () => {
    if (!onClick) return;
    onClick(item);
  };

  const cardTitle = (() => {
    if (title) {
      return title;
    }

    if (renderTitle) {
      return renderTitle(item);
    }

    return item?.[titleField];
  })();

  const renderLeftIcon = () => {
    let innerContent;

    const leftAbbreviation = abbreviation || item?.[abbreviationField];

    if (leftAbbreviation) {
      innerContent = (
        <Box className={classes.iconContainer}>
          {leftAbbreviation.toUpperCase()}
        </Box>
      );
    }
    if (icon) {
      innerContent = (
        <MiIcon
          path={icon}
          data-testid={iconTestId}
          size={size || 1}
          color={color}
        />
      );
    }
    if (!innerContent) return null;

    return (
      <Box className={classes.iconContainer} style={{ backgroundColor }}>
        {innerContent}
      </Box>
    );
  };

  return (
    <Box className={classes.root} {...flippedProps}>
      <Box
        className={`${classes.container} ${onClick ? classes.clickable : ""}`}
        style={item.containerStyles || containerStyles}
      >
        <Box
          className={classes.cardContainer}
          justifyContent={cardContainerJustifyContent}
          onClick={handleOnClick}
          data-testid={testID}
          style={cardContainerStyles}
        >
          {cardTitle && (
            <Box className={classes.leftCard}>
              <CardInfo
                title={cardTitle}
                fields={leftCardProps.fieldset}
                renderLeftIcon={renderLeftIcon}
                item={item}
              />
            </Box>
          )}
          <Box className={classes.sectionsContainer}>
            {sections?.length
              ? sections.map((section) => (
                  <MiFieldSetStacked
                    key={item?.id}
                    row={item}
                    testId={section?.testId}
                    fieldset={section?.fieldset}
                    showNulls={showNulls}
                    fieldsetStyle={{
                      ...section.fieldSetStyle,
                    }}
                    {...section}
                    doestCardExist={cardTitle}
                    doesActionsExist={renderItemActions}
                  />
                ))
              : null}
          </Box>
          {renderItemActions && renderItemActions(item)}
        </Box>
        {children}
      </Box>
      {rightAction ? (
        <Collapse in={showRightAction} orientation="horizontal" unmountOnExit>
          {rightAction(item, index)}
        </Collapse>
      ) : null}
    </Box>
  );
}

const useStyles = makeStyles()((theme, props) => ({
  root: {
    display: "flex",
    alignContent: "center",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    flex: 1,
    padding: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    borderRadius: 4,
    border: `1px solid ${theme.palette.grey.accent}`,
    boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.12)",
    ...(props?.isAlert && {
      backgroundColor:
        alphaColor(props?.alertColor, 0.1) || theme.palette.orange.light,
    }),
    ...(props?.isLate && { backgroundColor: theme.palette.red.light }),
    ...(!props?.isAlert &&
      !props?.isLate && { backgroundColor: theme.palette.common.white }),
  },
  sectionsContainer: {
    flex: 1,
    overflow: "hidden",
  },
  cardContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    flexWrap: "wrap",
    gap: 5,
    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start",
      flexDirection: "column",
    },
  },
  clickable: {
    "&:hover": {
      cursor: "pointer",
      backgroundColor: theme.palette.grey.light,
    },
  },
  row: {
    display: "flex",
  },
  iconContainer: {
    borderRadius: 5,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: theme.typography.fontWeightBold,
    height: 36,
    width: 36,
    color: theme.palette.common.white,
  },
  leftCard: {
    minWidth: props?.leftWidth || 250,
    maxWidth: props?.leftMaxWidth || 400,
    display: "flex",
    width: props?.leftWidth || "20vw",
    flexDirection: "row",
    alignItems: "center",
  },
}));
